import { createSlice, createEntityAdapter, type PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { Keyframe } from '@/modules/keyframes/types';
import type { RootState } from '@/store/rootState';

// Entity adapter for keyframes
const keyframesAdapter = createEntityAdapter<Keyframe>({
  sortComparer: (a, b) => a.timePosition - b.timePosition,
});

export interface KeyframesState extends ReturnType<typeof keyframesAdapter.getInitialState> {
  loadingByAssetId: Record<string, boolean>;
  idsByAssetId: Record<string, string[]>;
}

const initialState: KeyframesState = {
  ...keyframesAdapter.getInitialState(),
  loadingByAssetId: {},
  idsByAssetId: {},
};

const slice = createSlice({
  name: 'keyframes',
  initialState,
  reducers: {
    setAssetKeyframes: (
      state,
      action: PayloadAction<{ assetInstanceId: string; keyframes: Keyframe[] }>
    ) => {
      const { assetInstanceId, keyframes } = action.payload;
      const existingIds = state.idsByAssetId[assetInstanceId] || [];
      const newIds = new Set(keyframes.map((kf) => kf.id));
      const removedIds = existingIds.filter((id) => !newIds.has(id));
      if (removedIds.length > 0) {
        keyframesAdapter.removeMany(state, removedIds);
      }
      keyframesAdapter.upsertMany(state, keyframes);
      state.idsByAssetId[assetInstanceId] = keyframes.map((kf) => kf.id);
      state.loadingByAssetId[assetInstanceId] = false;
    },
    upsertKeyframe: (state, action: PayloadAction<Keyframe>) => {
      keyframesAdapter.upsertOne(state, action.payload);
      const assetId = action.payload.assetInstanceId;
      const list = state.idsByAssetId[assetId] || [];
      if (!list.includes(action.payload.id)) {
        state.idsByAssetId[assetId] = [...list, action.payload.id];
      }
    },
    removeKeyframe: (state, action: PayloadAction<{ id: string; assetInstanceId: string }>) => {
      keyframesAdapter.removeOne(state, action.payload.id);
      const list = state.idsByAssetId[action.payload.assetInstanceId] || [];
      state.idsByAssetId[action.payload.assetInstanceId] = list.filter((id) => id !== action.payload.id);
    },
    setAssetKeyframesLoading: (state, action: PayloadAction<{ assetInstanceId: string; loading: boolean }>) => {
      state.loadingByAssetId[action.payload.assetInstanceId] = action.payload.loading;
    },
    clearKeyframesForAsset: (state, action: PayloadAction<string>) => {
      const assetId = action.payload;
      const ids = state.idsByAssetId[assetId] || [];
      keyframesAdapter.removeMany(state, ids);
      delete state.idsByAssetId[assetId];
      delete state.loadingByAssetId[assetId];
    },
  },
});

export const {
  setAssetKeyframes,
  upsertKeyframe,
  removeKeyframe,
  setAssetKeyframesLoading,
  clearKeyframesForAsset,
} = slice.actions;

export default slice.reducer;

// Selectors
const selectors = keyframesAdapter.getSelectors<RootState>((state) => state.keyframes);

export const selectKeyframeById = (state: RootState, id: string) => selectors.selectById(state, id);

export const selectKeyframesByAssetId = (state: RootState, assetInstanceId: string): Keyframe[] => {
  const ids = state.keyframes.idsByAssetId[assetInstanceId] || [];
  return ids
    .map((id) => state.keyframes.entities[id])
    .filter((kf): kf is Keyframe => Boolean(kf))
    .sort((a, b) => a.timePosition - b.timePosition);
};

/**
 * Selector factory creating a memoized selector for keyframes by asset ID.
 * Using a factory pattern ensures each component instance maintains its own cache,
 * preventing 'unmemoized' warnings when multiple timeline items render simultaneously.
 */
export const makeSelectKeyframesByAssetId = () => createSelector(
  [
    (state: RootState) => state.keyframes.idsByAssetId,
    (state: RootState) => state.keyframes.entities,
    (state: RootState, assetInstanceId: string) => assetInstanceId
  ],
  (idsByAssetId, entities, assetInstanceId): Keyframe[] => {
    const ids = idsByAssetId[assetInstanceId] || [];
    return ids
      .map((id) => entities[id])
      .filter((kf): kf is Keyframe => Boolean(kf))
      .sort((a, b) => a.timePosition - b.timePosition);
  }
);

export const selectKeyframesLoadingByAssetId = (state: RootState, assetInstanceId: string) =>
  state.keyframes.loadingByAssetId[assetInstanceId] || false;


